import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Loader2, X } from 'lucide-react';
import {
    ModalOverlay,
    ModalPanel,
    ModalHeader,
    ModalTitle,
    ModalSubtitle,
    ModalCloseButton,
    ModalBody,
    ModalActions,
    ModalButton,
    ModalInput,
    ModalLabel,
    ModalStatusMessage,
} from './shared/Modal';

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

/** Inline spinner for buttons/rows whose action hits the Filevine API. */
const Spinner = styled(Loader2)`
    animation: ${spin} 0.9s linear infinite;
`;

/** A button label that shows a spinning icon while busy. */
const BusyLabel: React.FC<{ busy: boolean; busyText: string; children: React.ReactNode }> = ({
    busy,
    busyText,
    children,
}) =>
    busy ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Spinner size={14} />
            {busyText}
        </span>
    ) : (
        <>{children}</>
    );

const Explainer = styled.p`
    font-size: 13px;
    line-height: 1.5;
    color: #555;
    margin: 0;
`;

const CheckRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    user-select: none;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
    width: 16px;
    height: 16px;
    cursor: pointer;
`;

const LinkOut = styled.button`
    background: none;
    border: none;
    color: #4338ca;
    text-decoration: underline;
    cursor: pointer;
    font-size: 13px;
    padding: 0;
`;

const ResultsList = styled.ul`
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    max-height: 240px;
    overflow-y: auto;
`;

const ResultRow = styled.li<{ $disabled?: boolean }>`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    font-size: 13px;
    cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
    color: ${p => (p.$disabled ? '#999' : '#222')};
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
    &:hover {
        background: ${p => (p.$disabled ? 'transparent' : '#f5f5ff')};
    }
`;

const ResultMeta = styled.span`
    font-size: 12px;
    color: #888;
    white-space: nowrap;
`;

const LinkedBadge = styled.span`
    font-size: 11px;
    color: #92400e;
    background: #fef3c7;
    border-radius: 4px;
    padding: 1px 6px;
    white-space: nowrap;
`;

const ArchivedBadge = styled.span`
    font-size: 11px;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 1px 6px;
    white-space: nowrap;
`;

const SearchHint = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 4px;
`;

/** How long to wait after the last keystroke before hitting the API. */
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export interface ProjectSearchHit {
    projectId: number;
    projectName: string;
    number: string | null;
    phaseName: string | null;
    isArchived: boolean;
    alreadyLinkedCaseId: string | null;
}

export interface ResolvedProject {
    projectId: number;
    projectName: string;
    number: string | null;
    alreadyLinkedCaseId: string | null;
}

export interface LinkFilevineProjectModalProps {
    /** Server-side typeahead search over Filevine projects. */
    onSearch: (query: string) => Promise<ProjectSearchHit[]>;
    /** Resolve a project by numeric id (fallback when search misses). Throws on not-found. */
    onResolve: (params: { projectId?: number; name?: string }) => Promise<ResolvedProject>;
    /** Create the linked case. Returns the new case_id. */
    onLink: (params: {
        projectId: number;
        projectName: string;
        caseName?: string;
        useProjectName: boolean;
    }) => Promise<string>;
    /** Open an already-linked case (given its metadata path / case_id). */
    onOpenCase: (caseId: string) => void;
    onClose: () => void;
}

/**
 * Two-step "Link Filevine Project" dialog.
 *   Step 1 (find): type-ahead search against the Filevine project list
 *     (server-side searchTerm — no full-list scan). Rows already linked to a
 *     case are flagged and clicking them offers a jump to that case instead.
 *     All-digit queries can also be resolved directly as a project id.
 *   Step 2 (name): "use project name" checkbox above a case-name field, then
 *     Confirm Linking Project.
 */
export const LinkFilevineProjectModal: React.FC<LinkFilevineProjectModalProps> = ({
    onSearch,
    onResolve,
    onLink,
    onOpenCase,
    onClose,
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<ProjectSearchHit[]>([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const [resolved, setResolved] = useState<ResolvedProject | null>(null);
    const [useProjectName, setUseProjectName] = useState(true);
    const [caseName, setCaseName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Monotonic counter so a stale (slower) search response can't clobber a
    // newer one after the user kept typing.
    const searchSeq = useRef(0);

    useEffect(() => {
        const term = query.trim();
        if (term.length < MIN_QUERY_LENGTH) {
            setResults([]);
            setSearching(false);
            setSearched(false);
            return;
        }
        const seq = ++searchSeq.current;
        setSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const hits = await onSearch(term);
                if (searchSeq.current !== seq) return; // superseded
                setResults(hits);
                setSearched(true);
                setError(null);
            } catch (e) {
                if (searchSeq.current !== seq) return;
                setResults([]);
                setSearched(true);
                setError('Project search failed. You can still enter a numeric project id.');
            } finally {
                if (searchSeq.current === seq) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [query, onSearch]);

    const selectHit = (hit: ProjectSearchHit) => {
        setResolved({
            projectId: hit.projectId,
            projectName: hit.projectName,
            number: hit.number,
            alreadyLinkedCaseId: hit.alreadyLinkedCaseId,
        });
        setCaseName(hit.projectName);
        setError(null);
    };

    // Fallback path: an all-digits query can be looked up directly as an id
    // (covers projects the search endpoint doesn't surface by that string).
    const numericId = /^\d+$/.test(query.trim()) ? Number(query.trim()) : null;

    const handleResolveById = async () => {
        if (numericId === null || busy) return;
        setBusy(true);
        setError(null);
        try {
            const r = await onResolve({ projectId: numericId });
            setResolved(r);
            setCaseName(r.projectName);
        } catch (e) {
            setError(`No project found with id ${numericId}.`);
        } finally {
            setBusy(false);
        }
    };

    const handleConfirm = async () => {
        if (!resolved || busy) return;
        setBusy(true);
        setError(null);
        try {
            const caseId = await onLink({
                projectId: resolved.projectId,
                projectName: resolved.projectName,
                caseName: useProjectName ? undefined : caseName.trim(),
                useProjectName,
            });
            onClose();
            onOpenCase(caseId);
        } catch (e) {
            setError('Failed to link the project. Please try again.');
            setBusy(false);
        }
    };

    const alreadyLinked = resolved?.alreadyLinkedCaseId ?? null;
    const canConfirm =
        !!resolved && !alreadyLinked && (useProjectName || caseName.trim().length > 0);

    return (
        <ModalOverlay onClick={onClose}>
            <ModalPanel $width="520px" onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <div>
                        <ModalTitle>Link Filevine Project</ModalTitle>
                        {resolved && !alreadyLinked && (
                            <ModalSubtitle>
                                {resolved.projectName}
                                {resolved.number ? ` (${resolved.number})` : ''} · id {resolved.projectId}
                            </ModalSubtitle>
                        )}
                    </div>
                    <ModalCloseButton onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </ModalCloseButton>
                </ModalHeader>

                <ModalBody>
                    {!resolved && (
                        <>
                            <Explainer>
                                Linking creates a new BigPDF case tied to a Filevine project.
                                Nothing is imported automatically: use{' '}
                                <strong>Pull from Filevine</strong> inside the case to bring in a
                                specific document for processing, and the explicit{' '}
                                <strong>Push to Filevine</strong> action to send results back into
                                a <strong>pdf_processing_output</strong> folder. Original documents
                                are never modified.
                            </Explainer>
                            <ModalLabel htmlFor="fv-project-search">
                                Find a Filevine project
                            </ModalLabel>
                            <ModalInput
                                id="fv-project-search"
                                placeholder="Start typing a project name… (or a numeric id)"
                                value={query}
                                autoFocus
                                autoComplete="off"
                                onChange={e => setQuery(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && numericId !== null) {
                                        void handleResolveById();
                                    }
                                }}
                            />
                            {searching && (
                                <SearchHint>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <Spinner size={12} />
                                        Searching Filevine projects…
                                    </span>
                                </SearchHint>
                            )}
                            {results.length > 0 && (
                                <ResultsList>
                                    {results.map(hit => (
                                        <ResultRow
                                            key={hit.projectId}
                                            onClick={() => selectHit(hit)}
                                            title={`Filevine project id ${hit.projectId}`}
                                        >
                                            <span>
                                                {hit.projectName}
                                                {hit.number ? ` (${hit.number})` : ''}
                                            </span>
                                            {hit.alreadyLinkedCaseId ? (
                                                <LinkedBadge>already linked</LinkedBadge>
                                            ) : hit.isArchived ? (
                                                <ArchivedBadge>Archived</ArchivedBadge>
                                            ) : (
                                                hit.phaseName && <ResultMeta>{hit.phaseName}</ResultMeta>
                                            )}
                                        </ResultRow>
                                    ))}
                                </ResultsList>
                            )}
                            {!searching && searched && results.length === 0 && (
                                <SearchHint>
                                    No projects matched.
                                    {numericId !== null &&
                                        ' Press Enter to look this up as a project id.'}
                                </SearchHint>
                            )}
                        </>
                    )}

                    {resolved && alreadyLinked && (
                        <ModalStatusMessage $tone="info">
                            This project is already linked to a case. Only one case can exist per
                            project.{' '}
                            <LinkOut onClick={() => { onClose(); onOpenCase(alreadyLinked); }}>
                                Open that case
                            </LinkOut>
                            .
                        </ModalStatusMessage>
                    )}

                    {resolved && !alreadyLinked && (
                        <>
                            <CheckRow>
                                <Checkbox
                                    checked={useProjectName}
                                    disabled={busy}
                                    onChange={e => setUseProjectName(e.target.checked)}
                                />
                                Use project name as the case name
                            </CheckRow>
                            <ModalLabel htmlFor="fv-case-name">Case name</ModalLabel>
                            <ModalInput
                                id="fv-case-name"
                                value={useProjectName ? resolved.projectName : caseName}
                                disabled={useProjectName || busy}
                                onChange={e => setCaseName(e.target.value)}
                                placeholder="Case name"
                            />
                        </>
                    )}

                    {error && <ModalStatusMessage $tone="error">{error}</ModalStatusMessage>}
                </ModalBody>

                <ModalActions>
                    <ModalButton $variant="secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </ModalButton>
                    {!resolved ? (
                        numericId !== null && (
                            <ModalButton
                                $variant="primary"
                                onClick={() => void handleResolveById()}
                                disabled={busy}
                            >
                                <BusyLabel busy={busy} busyText="Looking up…">Look up id</BusyLabel>
                            </ModalButton>
                        )
                    ) : (
                        <ModalButton
                            $variant="primary"
                            onClick={() => void handleConfirm()}
                            disabled={!canConfirm || busy}
                        >
                            <BusyLabel busy={busy} busyText="Linking…">
                                Confirm Linking Project
                            </BusyLabel>
                        </ModalButton>
                    )}
                </ModalActions>
            </ModalPanel>
        </ModalOverlay>
    );
};
